import formStyles from '../formStyles.module.scss';

/**
 * Date input field that renders a native `type="date"` with a label.
 *
 * @remarks
 * This is a controlled input. The value is managed via the `value` prop and updated with `onChange`.
 * The `onChange` callback provides the current input value and the native change event for flexibility. You can then use the value to update your form state as needed or run any other function onchange.
 * Additional native `<input>` props (e.g. `min`, `max`, `disabled`) can be passed via `...rest`.
 *
 * If you provide `helpText` or `errorMessage`, they are programmatically associated via `aria-describedby`.
 * Providing an `errorMessage` marks the field invalid (`aria-invalid`) and renders the error text.
 * When `showRequired` is false, the input remains accessible via `aria-required` but the visual indicator is hidden using `aria-hidden`.
 *
 * @param props.id - Unique input ID used to associate the label
 * @param props.name - Field name used for form submission/validation
 * @param props.title - Visible label text
 * @param props.value - Current value (ISO format `YYYY-MM-DD`)
 * @param props.onChange Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native change event if you need it. SomeFunc is the function you want to call with the new value.
 * @param props.onBlur Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native blur event if you need it. SomeFunc is the function you want to call with the new value on blur.
 * @param props.required Optional - Marks the field required (default: false)
 * @param props.showRequired Optional - Controls whether the required asterisk (*) is visually displayed
 * @param props.helpText Optional - Helper text shown under the label
 * @param props.errorMessage Optional - Error message shown under the input
 * @param props.disabled Optional - Disables the input
 * @param props.formInstructionsId Optional - ID for additional accessibility description
 * @param props.rest - Any additional native `<input>` attributes
 *
 * @example
 * ```tsx
 * <DateInput
 *   id="checkin"
 *   name="checkin"
 *   title="Check-in date"
 *   value={form.checkin}
 *   onChange={(checkinValue) => setForm({ ...form, checkin: checkinValue })}
 *   min="2025-01-01"
 * />
 * ```
 */
const DateInput = ({
    id,
    name,
    title,
    value,
    onChange,
    onBlur,
    required = false,
    showRequired = true,
    helpText,
    errorMessage,
    disabled,
    formInstructionsId,
    ...rest
}: {
    id: string;
    name: string;
    title: string;
    value: string;
    onChange?: (value: string, e?: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (value: string, e?: React.FocusEvent<HTMLInputElement>) => void;
    required?: boolean;
    showRequired?: boolean;
    helpText?: string;
    errorMessage?: string;
    disabled?: boolean;
    formInstructionsId?: string;
} & Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onBlur' | 'value' | 'type'
>) => {
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const asterisk = (
        <span
            aria-hidden={!showRequired}
            className={formStyles.requiredAsterisk}
        >
            {' '}
            *
        </span>
    );
    const describedBy = [
        helpText ? helpId : null,
        errorMessage ? errorId : null,
        formInstructionsId || null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div data-testid="dateinput" className={formStyles.inputcontainer}>
            <label
                data-testid="dateinputlabel"
                htmlFor={id}
                className={formStyles.label}
            >
                {title}
                {required && showRequired ? asterisk : ''}
            </label>

            {helpText && (
                <p id={helpId} className={formStyles.help}>
                    {helpText}
                </p>
            )}

            <input
                data-testid="dateinputinput"
                type="date"
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange?.(e.target.value, e)}
                onBlur={(e) => onBlur?.(e.target.value, e)}
                required={required}
                disabled={disabled}
                aria-invalid={Boolean(errorMessage) || undefined}
                aria-describedby={describedBy || undefined}
                aria-required={required || undefined}
                className={`${formStyles.input} ${rest.className ?? ''}`}
                {...rest}
            />

            {errorMessage && (
                <p id={errorId} className={formStyles.error} role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default DateInput;
